import { useEffect, useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { ChangeClassButton } from "@/components/students/ChangeClassButton";

import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  CalendarClock,
  Plus,
  Pencil,
  Trash2,
  Clock,
  MapPin,
  Users,
  Check,
  X,
  UserPlus,
  Copy,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  FileDown,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { getMyTrainingCenter } from "@/lib/api/training-center.functions";
import { openClassesPdf, type ClassPdfGroup } from "@/lib/pdf/classes-pdf";
import { supabase } from "@/integrations/supabase/client";
import { listRentalModalities } from "@/lib/api/rentals.functions";
import {
  listSchedules,
  saveSchedule,
  deleteScheduleGroup,
} from "@/lib/api/teachers.functions";

import { listTeachers } from "@/lib/api/teachers.functions";
import {
  listEnrollableStudents,
  toggleEnrollment,
  listStudentsBySchedules,
} from "@/lib/api/enrollments.functions";

export const Route = createFileRoute("/_authenticated/m/horario-treinos")({
  component: SchedulesPage,
});

const DAYS = [
  { value: 0, label: "Dom" },
  { value: 1, label: "Seg" },
  { value: 2, label: "Ter" },
  { value: 3, label: "Qua" },
  { value: 4, label: "Qui" },
  { value: 5, label: "Sex" },
  { value: 6, label: "Sáb" },
];

type ScheduleRow = {
  id: string;
  name: string;
  day_of_week: number;
  start_time: string;
  end_time: string;
  start_date: string | null;
  end_date: string | null;
  category: string | null;
  location: string | null;
  modality_id: string | null;
  visible_to_parents?: boolean | null;
  allow_self_enroll?: boolean | null;
  rental_modalities: { id: string; name: string } | null;
  schedule_teachers: { teacher_id: string; teachers: { id: string; name: string } | null }[];
};

type Grouped = {
  key: string;
  name: string;
  start_time: string;
  end_time: string;
  start_date: string | null;
  end_date: string | null;
  category: string | null;
  location: string | null;
  modality_id: string | null;
  modalityName: string | null;
  days: number[];
  teacherIds: string[];
  teacherNames: string[];
  scheduleIds: string[];
  visible_to_parents: boolean;
  allow_self_enroll: boolean;
};

/** Converte caminho do bucket privado em URL assinada (ou devolve a URL pronta). */
async function toMediaUrl(value?: string | null): Promise<string | null> {
  if (!value) return null;
  if (/^https?:\/\//i.test(value) || value.startsWith("data:")) return value;
  const { data } = await supabase.storage
    .from("training_center_media")
    .createSignedUrl(value, 60 * 60);
  return data?.signedUrl ?? null;
}


function SchedulesPage() {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
          <CalendarClock className="w-7 h-7 text-primary" /> Horários de Treino
        </h1>
        <p className="text-muted-foreground text-sm">
          Gerencie os horários e turmas do centro de treinamento.
        </p>
      </div>
      <HorariosTab />

    </div>
  );
}

/* ============== HORÁRIOS E TURMAS ============== */

function HorariosTab() {
  const list = useServerFn(listSchedules);
  const del = useServerFn(deleteScheduleGroup);
  const [rows, setRows] = useState<ScheduleRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState<Grouped | null>(null);
  const [enrollFor, setEnrollFor] = useState<Grouped | null>(null);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [enrolled, setEnrolled] = useState<
    { schedule_id: string; id: string; name: string; category: string | null }[]
  >([]);
  const listEnrolled = useServerFn(listStudentsBySchedules);

  const reload = async () => {
    setLoading(true);
    try {
      const r = await list();
      const list2 = r.schedules as ScheduleRow[];
      setRows(list2);
      try {
        const ids = list2.map((x) => x.id);
        const er = await listEnrolled({ data: { schedule_ids: ids } });
        setEnrolled(er.rows as any);
      } catch { /* ignore */ }
    } catch (e: any) { toast.error(e?.message || "Erro ao carregar"); } finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, []);

  const grouped: Grouped[] = useMemo(() => {
    const map = new Map<string, Grouped>();
    for (const r of rows) {
      const key = `${r.name}|${r.start_time}|${r.end_time}`;
      if (!map.has(key)) {
        map.set(key, {
          key,
          name: r.name,
          start_time: r.start_time,
          end_time: r.end_time,
          start_date: r.start_date,
          end_date: r.end_date,
          category: r.category,
          location: r.location,
          modality_id: r.modality_id ?? null,
          modalityName: r.rental_modalities?.name ?? null,
          days: [],
          teacherIds: [],
          teacherNames: [],
          scheduleIds: [],
          visible_to_parents: r.visible_to_parents !== false,
          allow_self_enroll: r.allow_self_enroll === true,
        });
      }
      const g = map.get(key)!;
      if (!g.scheduleIds.includes(r.id)) g.scheduleIds.push(r.id);
      if (!g.days.includes(r.day_of_week)) g.days.push(r.day_of_week);
      for (const st of r.schedule_teachers ?? []) {
        if (st.teachers && !g.teacherIds.includes(st.teachers.id)) {
          g.teacherIds.push(st.teachers.id);
          g.teacherNames.push(st.teachers.name);
        }
      }
    }
    return Array.from(map.values()).sort((a, b) => a.start_time.localeCompare(b.start_time));
  }, [rows]);

  const studentsByGroup = useMemo(() => {
    const out: Record<string, { id: string; name: string; category: string | null }[]> = {};
    for (const g of grouped) {
      const seen = new Set<string>();
      const arr: { id: string; name: string; category: string | null }[] = [];
      for (const e of enrolled) {
        if (!g.scheduleIds.includes(e.schedule_id)) continue;
        if (seen.has(e.id)) continue;
        seen.add(e.id);
        arr.push({ id: e.id, name: e.name, category: e.category });
      }
      arr.sort((a, b) => a.name.localeCompare(b.name));
      out[g.key] = arr;
    }
    return out;
  }, [grouped, enrolled]);

  const groupLabel = (g: Grouped) => {
    const days = DAYS.filter((d) => g.days.includes(d.value)).map((d) => d.label).join(", ");
    return `${g.name}${g.category ? ` · ${g.category}` : ""}${days ? ` · ${days}` : ""} ${g.start_time.slice(0, 5)}-${g.end_time.slice(0, 5)}`;
  };

  const groupText = (g: Grouped) => {
    const days = DAYS.filter((d) => g.days.includes(d.value)).map((d) => d.label).join(", ");
    const head = `*${g.name}*\n${g.start_time.slice(0, 5)} — ${g.end_time.slice(0, 5)}${days ? ` · ${days}` : ""}${g.modalityName || g.location ? `\n${g.modalityName ?? g.location}` : ""}`;
    const arr = studentsByGroup[g.key] ?? [];
    const body = arr.length
      ? arr.map((s, i) => `${i + 1}. ${s.name}${s.category ? ` (${s.category})` : ""}`).join("\n")
      : "Nenhum aluno matriculado.";
    return `${head}\nAlunos (${arr.length}):\n${body}`;
  };

  const doCopy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copiado para a área de transferência");
    } catch {
      toast.error("Não foi possível copiar");
    }
  };

  const copyGroup = (g: Grouped) => doCopy(groupText(g));
  const copyAll = () => doCopy(grouped.map(groupText).join("\n\n"));

  /* -------- PDF das turmas -------- */
  const getTcFn = useServerFn(getMyTrainingCenter);
  const [pdfBusy, setPdfBusy] = useState<string | null>(null);

  const toPdfGroup = (g: Grouped): ClassPdfGroup => ({
    name: g.name,
    category: g.category,
    timeRange: `${g.start_time.slice(0, 5)} — ${g.end_time.slice(0, 5)}`,
    days: DAYS.filter((d) => g.days.includes(d.value)).map((d) => d.label).join(", "),
    location: g.modalityName ?? g.location ?? null,
    students: (studentsByGroup[g.key] ?? []).map((s) => ({ name: s.name, category: s.category })),
  });

  const generatePdf = async (list: Grouped[], busyKey: string) => {
    if (!list.length) {
      toast.error("Nenhuma turma para gerar o PDF.");
      return;
    }
    setPdfBusy(busyKey);
    try {
      const tcRes: any = await getTcFn().catch(() => null);
      const tc = tcRes?.trainingCenter ?? null;
      await openClassesPdf({
        tcName: tc?.name ?? "",
        tcLogoUrl: await toMediaUrl(tc?.logo_url ?? null),
        groups: list.map(toPdfGroup),
      });
    } catch (e: any) {
      toast.error(e?.message ?? "Erro ao gerar PDF");
    } finally {
      setPdfBusy(null);
    }
  };


  const onDelete = async (g: Grouped) => {
    if (!confirm(`Excluir horário "${g.name}"?`)) return;
    try {
      await del({ data: { name: g.name, start_time: g.start_time, end_time: g.end_time } });
      toast.success("Horário excluído");
      reload();
    } catch (e: any) { toast.error(e?.message || "Erro"); }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap justify-end gap-2">
        <Button variant="outline" onClick={copyAll} disabled={grouped.length === 0}>
          <Copy className="w-4 h-4 mr-2" /> Copiar todas as turmas
        </Button>
        <Button
          variant="outline"
          onClick={() => generatePdf(grouped, "__all__")}
          disabled={grouped.length === 0 || pdfBusy !== null}
        >
          {pdfBusy === "__all__" ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <FileDown className="w-4 h-4 mr-2" />
          )}
          PDF de todas as turmas
        </Button>
        <Button onClick={() => { setEditing(null); setModal(true); }}>
          <Plus className="w-4 h-4 mr-2" /> Novo Horário
        </Button>
      </div>
      {loading ? (
        <div className="h-32 bg-muted/40 rounded-xl animate-pulse" />
      ) : grouped.length === 0 ? (
        <Card>
          <CardContent className="p-10 text-center border-dashed">
            <p className="text-muted-foreground">Nenhum horário cadastrado. Comece criando um novo horário de treino.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {grouped.map((g) => (
            <Card key={g.key} className="hover:shadow-md transition-shadow">
              <CardContent className="p-5 space-y-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <h3 className="font-bold truncate">{g.name}</h3>
                  </div>

                  <div className="flex gap-1 shrink-0">
                    <Button variant="ghost" size="icon" onClick={() => { setEditing(g); setModal(true); }}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" className="text-destructive" onClick={() => onDelete(g)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <div className="text-sm text-muted-foreground flex items-center gap-2">
                  <Clock className="w-4 h-4" /> {g.start_time.slice(0,5)} — {g.end_time.slice(0,5)}
                </div>
                {(g.modalityName || g.location) && (
                  <div className="text-sm text-muted-foreground flex items-center gap-2">
                    <MapPin className="w-4 h-4" /> {g.modalityName ?? g.location}
                  </div>
                )}
                <div className="flex flex-wrap gap-1">
                  {DAYS.map((d) => (
                    <span
                      key={d.value}
                      className={`text-xs px-2 py-1 rounded-md border ${g.days.includes(d.value) ? "bg-primary text-primary-foreground border-primary" : "bg-muted text-muted-foreground"}`}
                    >
                      {d.label}
                    </span>
                  ))}
                </div>
                {g.teacherNames.length > 0 && (
                  <div className="text-xs text-muted-foreground flex items-center gap-1 pt-2 border-t">
                    <Users className="w-3 h-3" /> {g.teacherNames.join(", ")}
                  </div>
                )}

                <div className="pt-2 border-t space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <button
                      type="button"
                      className="text-sm font-medium flex items-center gap-1 hover:underline"
                      onClick={() =>
                        setExpanded((e) => ({ ...e, [g.key]: !e[g.key] }))
                      }
                    >
                      <Users className="w-4 h-4" />
                      {(studentsByGroup[g.key] ?? []).length} aluno(s)
                      {expanded[g.key] ? (
                        <ChevronUp className="w-3 h-3" />
                      ) : (
                        <ChevronDown className="w-3 h-3" />
                      )}
                    </button>
                    <div className="flex items-center gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => copyGroup(g)}
                      >
                        <Copy className="w-4 h-4 mr-1" /> Copiar
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => generatePdf([g], g.key)}
                        disabled={pdfBusy !== null}
                      >
                        {pdfBusy === g.key ? (
                          <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                        ) : (
                          <FileDown className="w-4 h-4 mr-1" />
                        )}
                        PDF
                      </Button>
                    </div>
                  </div>
                  {expanded[g.key] && (
                    <ol className="text-sm space-y-1 list-decimal list-inside max-h-48 overflow-auto">
                      {(studentsByGroup[g.key] ?? []).length === 0 ? (
                        <li className="list-none text-muted-foreground text-xs">
                          Nenhum aluno matriculado.
                        </li>
                      ) : (
                        (studentsByGroup[g.key] ?? []).map((s) => (
                          <li key={s.id} className="flex items-center justify-between gap-2 py-0.5">
                            <span className="min-w-0 truncate">
                              {s.name}
                              {s.category ? (
                                <span className="text-muted-foreground text-xs">
                                  {" "}
                                  · {s.category}
                                </span>
                              ) : null}
                            </span>
                            <ChangeClassButton
                              studentId={s.id}
                              studentName={s.name}
                              currentClassLabel={groupLabel(g)}
                              currentClassKey={g.key}
                              currentScheduleIds={g.scheduleIds}
                              classes={grouped.map((gg) => ({
                                key: gg.key,
                                label: groupLabel(gg),
                                scheduleIds: gg.scheduleIds,
                              }))}
                              onChanged={reload}
                            />
                          </li>
                        ))
                      )}
                    </ol>
                  )}
                </div>

                <Button
                  size="sm"
                  variant="outline"
                  className="w-full mt-2"
                  onClick={() => setEnrollFor(g)}
                >
                  <UserPlus className="w-4 h-4 mr-2" /> Matricular Aluno
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}


      <ScheduleDialog
        key={editing?.key ?? "new"}
        open={modal}
        onOpenChange={setModal}
        editing={editing}
        allRows={rows}
        onSaved={() => { setModal(false); reload(); }}
      />

      <EnrollDialog
        group={enrollFor}
        groups={grouped}
        enrollments={enrolled}
        onClose={() => { setEnrollFor(null); reload(); }}
      />
    </div>
  );
}

function overlaps(aS: string, aE: string, bS: string, bE: string) {
  return aS.slice(0, 5) < bE.slice(0, 5) && bS.slice(0, 5) < aE.slice(0, 5);
}

function EnrollDialog({
  group,
  groups,
  enrollments,
  onClose,
}: {
  group: Grouped | null;
  groups: Grouped[];
  enrollments: { schedule_id: string; id: string }[];
  onClose: () => void;
}) {
  const list = useServerFn(listEnrollableStudents);
  const toggle = useServerFn(toggleEnrollment);
  const [students, setStudents] = useState<any[]>([]);
  const [filter, setFilter] = useState("");
  const [scope, setScope] = useState<"turma" | "sem" | "todos">("turma");
  const [loading, setLoading] = useState(false);
  const [links, setLinks] = useState<{ schedule_id: string; id: string }[]>([]);

  const open = !!group;
  const primaryId = group?.scheduleIds[0];

  useEffect(() => {
    if (!open) return;
    setLinks(enrollments);
  }, [open, enrollments]);

  useEffect(() => {
    if (!open || !primaryId) return;
    setScope("turma");
    setFilter("");
    setLoading(true);
    list({ data: { schedule_id: primaryId } })
      .then((r) => setStudents(r.students))
      .catch((e: any) => toast.error(e?.message || "Erro"))
      .finally(() => setLoading(false));
  }, [open, primaryId]);

  // Turmas em que cada aluno está matriculado
  const groupsByStudent = useMemo(() => {
    const map: Record<string, Grouped[]> = {};
    for (const l of links) {
      const g = groups.find((x) => x.scheduleIds.includes(l.schedule_id));
      if (!g) continue;
      const arr = (map[l.id] ??= []);
      if (!arr.some((x) => x.key === g.key)) arr.push(g);
    }
    return map;
  }, [links, groups]);

  const conflictsFor = (studentId: string) => {
    if (!group) return [] as Grouped[];
    return (groupsByStudent[studentId] ?? []).filter(
      (g) =>
        g.key !== group.key &&
        g.days.some((d) => group.days.includes(d)) &&
        overlaps(g.start_time, g.end_time, group.start_time, group.end_time),
    );
  };

  const describe = (g: Grouped) =>
    `${g.name} (${DAYS.filter((d) => g.days.includes(d.value)).map((d) => d.label).join(", ")} ${g.start_time.slice(0, 5)}—${g.end_time.slice(0, 5)})`;

  const onToggle = async (studentId: string, enroll: boolean) => {
    if (!group) return;
    if (enroll) {
      const conf = conflictsFor(studentId);
      if (conf.length) {
        const ok = confirm(
          `Conflito de horário!\n\nO aluno já está matriculado em:\n${conf
            .map((g) => `• ${describe(g)}`)
            .join("\n")}\n\nO horário coincide com ${describe(group)}.\n\nDeseja matricular mesmo assim?`,
        );
        if (!ok) return;
      }
    }
    try {
      await Promise.all(
        group.scheduleIds.map((sid) =>
          toggle({ data: { student_id: studentId, schedule_id: sid, enroll } }),
        ),
      );
      setStudents((arr) =>
        arr.map((s) => (s.id === studentId ? { ...s, enrolled: enroll } : s)),
      );
      setLinks((arr) =>
        enroll
          ? [
              ...arr,
              ...group.scheduleIds.map((sid) => ({ schedule_id: sid, id: studentId })),
            ]
          : arr.filter(
              (l) => !(l.id === studentId && group.scheduleIds.includes(l.schedule_id)),
            ),
      );
      toast.success(enroll ? "Matriculado" : "Removido");
    } catch (e: any) { toast.error(e?.message || "Erro"); }
  };

  const filtered = students.filter((s) => {
    if (scope === "sem" && (groupsByStudent[s.id]?.length ?? 0) > 0) return false;
    if (scope === "turma" && !s.enrolled) return false;
    return (
      !filter ||
      s.name.toLowerCase().includes(filter.toLowerCase()) ||
      (s.category ?? "").toLowerCase().includes(filter.toLowerCase())
    );
  });

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Matricular Alunos · {group?.name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <Input
            placeholder="Buscar aluno por nome ou categoria..."
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
          <div className="flex flex-wrap items-center gap-2">
            {([
              { key: "turma", label: "Desta turma" },
              { key: "sem", label: "Sem turma" },
              { key: "todos", label: "Todos" },
            ] as const).map((opt) => (
              <Button
                key={opt.key}
                type="button"
                size="sm"
                variant={scope === opt.key ? "default" : "outline"}
                onClick={() => setScope(opt.key)}
              >
                {opt.label}
              </Button>
            ))}
            <span className="text-xs text-muted-foreground">
              {scope === "turma"
                ? "Mostrando os alunos desta turma"
                : scope === "sem"
                  ? "Mostrando apenas alunos sem turma"
                  : "Mostrando todos os alunos"}
            </span>
          </div>
          {loading ? (
            <div className="h-32 bg-muted/40 rounded animate-pulse" />
          ) : filtered.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">
              Nenhum aluno encontrado.
            </p>
          ) : (
            <div className="max-h-80 overflow-y-auto space-y-2">
              {filtered.map((s) => {
                const enrolledGroups = groupsByStudent[s.id] ?? [];
                const conf = conflictsFor(s.id);
                return (
                  <div
                    key={s.id}
                    className={`flex items-center justify-between gap-2 p-2 border rounded-md ${conf.length ? "border-destructive/50 bg-destructive/5" : ""}`}
                  >
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{s.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {s.category ?? "—"}
                        {s.jersey_number ? ` · Camisa ${s.jersey_number}` : ""}
                      </div>
                      {enrolledGroups.length > 0 && (
                        <div className="text-xs text-muted-foreground mt-1">
                          Turmas: {enrolledGroups.map(describe).join(" | ")}
                        </div>
                      )}
                      {conf.length > 0 && (
                        <div className="text-xs text-destructive font-medium mt-1 flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3" /> Conflito de horário com{" "}
                          {conf.map((g) => g.name).join(", ")}
                        </div>
                      )}
                    </div>
                    <Button
                      size="sm"
                      variant={s.enrolled ? "secondary" : "default"}
                      onClick={() => onToggle(s.id, !s.enrolled)}
                    >
                      {s.enrolled ? (
                        <><X className="w-3 h-3 mr-1" /> Remover</>
                      ) : (
                        <><Check className="w-3 h-3 mr-1" /> Matricular</>
                      )}
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ScheduleDialog({
  open, onOpenChange, editing, allRows, onSaved,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  editing: Grouped | null;
  allRows: ScheduleRow[];
  onSaved: () => void;
}) {
  const save = useServerFn(saveSchedule);
  const listT = useServerFn(listTeachers);
  const listMods = useServerFn(listRentalModalities);

  const today = new Date().toISOString().split("T")[0];
  const defaultEndDate = "2099-12-31";

  const [name, setName] = useState(editing?.name ?? "");
  const [location, setLocation] = useState(editing?.location ?? "");
  const [modalityId, setModalityId] = useState<string>(editing?.modality_id ?? "");
  const [modalities, setModalities] = useState<{ id: string; name: string }[]>([]);
  const [startDate, setStartDate] = useState(editing?.start_date ?? today);
  const [endDate, setEndDate] = useState(editing?.end_date ?? defaultEndDate);
  const [category, setCategory] = useState(editing?.category ?? "");
  const [teacherIds, setTeacherIds] = useState<string[]>(editing?.teacherIds ?? []);
  const [days, setDays] = useState<number[]>(editing?.days ?? []);
  const [startTime, setStartTime] = useState(editing?.start_time?.slice(0,5) ?? "");
  const [endTime, setEndTime] = useState(editing?.end_time?.slice(0,5) ?? "");
  const [teachers, setTeachers] = useState<{ id: string; name: string }[]>([]);
  const [visibleToParents, setVisibleToParents] = useState(editing ? editing.visible_to_parents : true);
  const [allowSelfEnroll, setAllowSelfEnroll] = useState(editing ? editing.allow_self_enroll : false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const [te, mo] = await Promise.all([listT(), listMods()]);
        setTeachers((te.teachers as any).map((x: any) => ({ id: x.id, name: x.name })));
        setModalities(((mo as any).modalities ?? []).filter((m: any) => m.active !== false).map((m: any) => ({ id: m.id, name: m.name })));
      } catch (e: any) { toast.error(e?.message || "Erro"); }
    })();
  }, [open]);


  const toggleDay = (d: number) =>
    setDays((arr) => arr.includes(d) ? arr.filter((x) => x !== d) : [...arr, d]);

  const toggleTeacher = (id: string) =>
    setTeacherIds((arr) => arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id]);

  const conflicts = useMemo(() => {
    if (!startTime || !endTime || days.length === 0) return [] as ScheduleRow[];
    const editKeys = new Set(
      editing ? editing.scheduleIds : []
    );
    return allRows.filter((r) => {
      if (editKeys.has(r.id)) return false;
      if (!days.includes(r.day_of_week)) return false;
      const sameCourt = modalityId
        ? r.modality_id === modalityId
        : (!r.modality_id && (location || "").trim().toLowerCase() === (r.location || "").trim().toLowerCase() && !!(location || "").trim());
      if (!sameCourt) return false;
      const a1 = startTime, a2 = endTime;
      const b1 = (r.start_time || "").slice(0, 5), b2 = (r.end_time || "").slice(0, 5);
      return a1 < b2 && b1 < a2;
    });
  }, [allRows, days, startTime, endTime, modalityId, location, editing]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) { toast.error("Nome obrigatório"); return; }
    if (days.length === 0) { toast.error("Selecione ao menos um dia"); return; }
    if (!startTime || !endTime) { toast.error("Defina hora início e fim"); return; }
    setSaving(true);
    try {
      await save({ data: {
        edit: editing ? { name: editing.name, start_time: editing.start_time, end_time: editing.end_time } : null,
        name: name.trim(),
        location: location || null,
        modality_id: modalityId || null,
        start_date: startDate || null,
        end_date: endDate || null,
        category: category || null,
        training_type_id: null,
        teacher_ids: teacherIds,
        days_of_week: days,
        start_time: startTime,
        end_time: endTime,
        visible_to_parents: visibleToParents,
        allow_self_enroll: allowSelfEnroll,
      } });
      toast.success(editing ? "Horário atualizado" : "Horário criado");
      onSaved();
    } catch (e: any) { toast.error(e?.message || "Erro ao salvar"); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editing ? "Editar Horário" : "Novo Horário"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4 pt-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Nome da Turma/Horário *</Label>
              <Input required value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Quadra / Espaço</Label>
              <Select value={modalityId || "__none__"} onValueChange={(v) => setModalityId(v === "__none__" ? "" : v)}>
                <SelectTrigger><SelectValue placeholder="Selecione a quadra..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Sem quadra definida</SelectItem>
                  {modalities.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                </SelectContent>
              </Select>
              {modalities.length === 0 && (
                <p className="text-xs text-muted-foreground">Cadastre as quadras em Reserva de Espaço → Cadastro de Espaço.</p>
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Data de Início *</Label>
              <Input type="date" required value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Data de Fim</Label>
              <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Categoria</Label>
              <Input
                list="category-options"
                placeholder="Ex: Sub-15"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
              />
              <datalist id="category-options">
                {Array.from({ length: 16 }, (_, i) => `Sub-${i + 5}`).map((c) => (
                  <option key={c} value={c} />
                ))}
              </datalist>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Professores Responsáveis</Label>
            <div className="flex flex-wrap gap-2 p-2 border rounded-md min-h-[44px]">
              {teachers.length === 0 ? (
                <span className="text-xs text-muted-foreground p-2">Cadastre professores em "Corpo Docente".</span>
              ) : teachers.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => toggleTeacher(t.id)}
                  className={`text-xs px-3 py-1.5 rounded-full border transition ${teacherIds.includes(t.id) ? "bg-primary text-primary-foreground border-primary" : "bg-muted hover:bg-muted/70"}`}
                >
                  {t.name}
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-2">
            <Label>Dias da Semana *</Label>
            <div className="flex flex-wrap gap-2">
              {DAYS.map((d) => (
                <button
                  key={d.value}
                  type="button"
                  onClick={() => toggleDay(d.value)}
                  className={`text-sm px-4 py-2 rounded-md border transition ${days.includes(d.value) ? "bg-primary text-primary-foreground border-primary" : "bg-background hover:bg-accent"}`}
                >
                  {d.label}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Hora Início *</Label>
              <Input type="time" required value={startTime} onChange={(e) => setStartTime(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Hora Fim *</Label>
              <Input type="time" required value={endTime} onChange={(e) => setEndTime(e.target.value)} />
            </div>
          </div>
          <div className="space-y-3 rounded-md border p-3 bg-muted/30">
            <Label className="text-sm">Visibilidade para o responsável</Label>
            <label className="flex items-start gap-2 text-sm cursor-pointer">
              <Checkbox checked={visibleToParents} onCheckedChange={(v) => setVisibleToParents(!!v)} />
              <span>
                Deixar este horário <strong>visível</strong> no portal do responsável
                <span className="block text-xs text-muted-foreground">Padrão: visível.</span>
              </span>
            </label>
            <label className="flex items-start gap-2 text-sm cursor-pointer">
              <Checkbox checked={allowSelfEnroll} onCheckedChange={(v) => setAllowSelfEnroll(!!v)} />
              <span>
                Permitir que o responsável <strong>faça a matrícula</strong> nesta turma
                <span className="block text-xs text-muted-foreground">Padrão: não permitido (alocação apenas pelo CT).</span>
              </span>
            </label>
          </div>
          {conflicts.length > 0 && (
            <div className="rounded-md border-2 border-destructive bg-destructive/10 p-3 space-y-1">
              <p className="flex items-center gap-2 text-sm font-bold uppercase text-destructive tracking-wide">
                <AlertTriangle className="h-5 w-5" /> ATENÇÃO: JÁ EXISTE TREINO OU RESERVA NESTE HORÁRIO E LOCAL!
              </p>
              {conflicts.map((c) => (
                <p key={c.id} className="text-xs text-destructive">
                  {DAYS.find((d) => d.value === c.day_of_week)?.label} • {(c.start_time || "").slice(0,5)}–{(c.end_time || "").slice(0,5)} • {c.name}
                  {c.rental_modalities?.name ? ` • ${c.rental_modalities.name}` : c.location ? ` • ${c.location}` : ""}
                </p>
              ))}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button type="submit" disabled={saving}>{saving ? "Salvando..." : "Salvar"}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

